import { AxiosInstance, AxiosResponse } from 'axios'
import { inject } from 'tsyringe'
import { httpToken } from '~/constants/dependency-injection/tokens'
import { containerScoped } from '~/decorators/dependency-container'
import { ActionResult } from '~/models/shared/result'
import { invalidBodyError } from '~/services/errors'
import {
  PriceStatsResult,
  QuickStatsResult,
  VehiclePriceStatsResult
} from '~/models/classified/stats'

@containerScoped()
export default class ClassifiedStatsService {
  constructor(@inject(httpToken) private http: AxiosInstance) {}

  async getQuickStats(classifiedId: number): Promise<QuickStatsResult> {
    const response: AxiosResponse = await this.http.get(
      `/api/classifieds/${classifiedId}/quick-stats/`
    )
    const { data: body } = response
    if (!(body && body.data)) {
      throw invalidBodyError(body)
    }
    return this.formatQuickStatsResponse(body.data)
  }

  async resetQuickStats(classifiedId: number): Promise<ActionResult> {
    const response: AxiosResponse = await this.http.delete(
      `/api/classifieds/${classifiedId}/quick-stats/`
    )
    const { data: body } = response
    if (!body) {
      throw invalidBodyError(body)
    }
    return body
  }

  async getPriceStats(classifiedId: number): Promise<PriceStatsResult> {
    const response: AxiosResponse = await this.http.get(
      `/api/classifieds/${classifiedId}/price-stats/`
    )
    const { data: body } = response
    if (!(body && body.data)) {
      throw invalidBodyError(body)
    }
    return this.formatPriceStartsResponse(body.data)
  }

  async getVehiclePriceStats(
    classifiedId: number
  ): Promise<VehiclePriceStatsResult> {
    const response: AxiosResponse = await this.http.get(
      `/api/vehicles/${classifiedId}/price-stats/`
    )
    const { data: body } = response
    if (!(body && body.data)) {
      throw invalidBodyError(body)
    }
    return this.formatVehiclePriceStatsResponse(body.data)
  }

  private formatPriceStartsResponse(r: any): PriceStatsResult {
    return {
      maxPrice: r.price.max_price,
      minPrice: r.price.min_price,
      changes: r.price.changes,
      currentPrice: r.price.current_price,
      tier: r.perks?.tier
    }
  }

  private formatVehiclePriceStatsResponse(r: any): VehiclePriceStatsResult {
    return {
      currentPrices: {
        activeClassifiedsStats: r.current_prices.active,
        deletedClassifiedsStats: r.current_prices.deleted,
        allClassifiedStats: r.current_prices.all,
        statsArguments: r.current_prices.arguments,
        searchSeoUrl: r.current_prices.seo_url,
        pricesBuckets: r.current_prices.prices_buckets
      },
      tier: r.perks?.tier,
      priceHistory: r.price_history
    }
  }

  private formatQuickStatsResponse(r: any): QuickStatsResult {
    return {
      created: r.created,
      listViews: r.list_views,
      modified: r.modified,
      parked: r.parked,
      statisticsReset: r.statistics_reset,
      views: r.views
    }
  }
}
